import React from 'react';
import { AlertTriangle, RefreshCw, X } from 'lucide-react';

class SurveyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Survey Error:', error, errorInfo);

    // Log to error reporting service in production
    if (process.env.NODE_ENV === 'production') {
      // Example: logErrorToService(error, { component: 'Survey', ...errorInfo });
    }

    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleClose = () => {
    // Close the survey modal if onClose is provided
    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  render() {
    if (this.state.hasError) {
      const isRetryable = this.state.retryCount < 3;

      return (
        <div className="relative bg-black/95 backdrop-blur-xl rounded-2xl p-8 max-w-md mx-auto">
          {/* Close button */}
          <button
            onClick={this.handleClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>

          <div className="text-center">
            {/* Error icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>

            {/* Error message */}
            <h3 className="text-xl font-semibold text-white mb-2">
              Survey Error
            </h3>

            <p className="text-white/60 mb-6">
              {this.state.error?.message === 'Network Error'
                ? 'Unable to save your survey response. Please check your internet connection and try again.'
                : 'Something went wrong with the survey. Your progress has been saved.'}
            </p>

            {/* Development error details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="text-left mb-6 p-4 bg-white/5 rounded-lg">
                <summary className="cursor-pointer text-sm font-medium text-white/80">
                  Error Details (Dev)
                </summary>
                <pre className="mt-2 text-xs text-white/60 overflow-x-auto whitespace-pre-wrap">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            {/* Action buttons */}
            <div className="flex gap-3 justify-center">
              {isRetryable && (
                <button
                  onClick={this.handleReset}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                  {this.state.retryCount > 0 && (
                    <span className="text-xs text-gray-500">
                      ({3 - this.state.retryCount} left)
                    </span>
                  )}
                </button>
              )}

              <button
                onClick={this.handleClose}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors font-medium"
              >
                Close Survey
              </button>
            </div>

            {!isRetryable && (
              <p className="mt-4 text-sm text-white/40">
                Maximum retry attempts reached. Please refresh the page if the problem persists.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SurveyErrorBoundary;